// src/pages/AdminEntityFormationViewPage.tsx
// This is essentially the same as EntityFormationViewPage but for admin access

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { loadEntityFormationSubmissionById } from '../lib/intake/entityFormationSubmitApi';
import { logDataAccess } from '../lib/audit/auditLogger';
import { ENTITY_INTAKE_SECTIONS, getVisibleFieldKeysForStep, type EntityIntakeFieldKey } from '../lib/intake/entityFormationFields';
import { US_STATES } from '../lib/usStates';
import type { EntityIntakeDefaults } from '../lib/intake/entityFormationDefaults';
import {
  OWNER_TYPE_OPTIONS,
  type Option,
} from '../lib/intake/entityFormationOptions';

/**
 * Gets the human-readable label for a field
 */
function getFieldLabel(fieldKey: EntityIntakeFieldKey): string {
  const labels: Record<string, string> = {
    full_legal_name: 'Full Legal Name',
    preferred_name: 'Preferred Name',
    phone_number: 'Phone Number',
    email_address: 'Email Address',
    street_address: 'Street Address 1',
    street_address_2: 'Street Address 2',
    city: 'City',
    state: 'State',
    zip_code: 'ZIP Code',
    state_of_residence: 'State of Residence',
    us_citizen: 'US Citizen',
    citizenship_status: 'Citizenship Status',
    ssn_itin: 'SSN/ITIN (Optional)',
    legal_business_name: 'Legal Business Name',
    alternate_business_names: 'Alternate Business Names',
    business_description: 'Business Description',
    industry: 'Industry / Sector',
    industry_other: 'Other Industry',
    timeline: 'Timeline',
    revenue_expectations: '1st Year Revenue Expectations',
    growth_outlook: 'Growth Outlook',
    strategic_intent: 'Strategic Intent',
    strategic_intent_other: 'Other Strategic Intent',
    number_of_owners: 'Number of Owners',
    owners: 'Owners',
    management_preference: 'Management Preference',
    future_ownership_changes: 'Future Ownership Changes',
    operating_states: 'Operating States',
    operates_outside_formation_state: 'Operates Outside Formation State',
    has_employees: 'Has Employees',
    uses_contractors: 'Uses 1099 Contractors',
    sells_online: 'Sells Online',
    serves_nationally: 'Serves Nationally',
    risk_factors: 'Risk Factors',
    liability_risk: 'Liability Risk',
    has_business_bank_account: 'Has Business Bank Account',
    uses_bookkeeping_software: 'Uses Bookkeeping Software',
    bookkeeping_software_name: 'Bookkeeping Software Name',
    discussed_tax_planning: 'Discussed Tax Planning',
    owns_domain: 'Owns Domain',
    domain_name: 'Domain Name',
    website_timeline: 'Website Timeline',
    interested_in_brand_protection: 'Interested in Brand Protection',
    initial_funding: 'Initial Funding',
    initial_funding_other: 'Other Initial Funding',
    plans_to_raise_capital: 'Plans to Raise Capital',
    additional_services: 'Additional Services Interest',
    acknowledge_not_attorney_or_cpa: 'I understand that Help Keep My Money LLC provides business management consulting, is not acting as my attorney or CPA, and is not licensed to provide legal or financial advice.',
    acknowledge_entity_formation_scope: 'I understand entity formation is only one part of building a compliant and sustainable business.',
    certify_accuracy: 'I confirm the information provided is accurate to the best of my knowledge.',
    client_signature: 'Client Signature (must match Client Full Legal Name)',
    signature_date: 'Signature Date',
  };

  return labels[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Gets options for a specific section and field
 */
function getSectionOptions(section: typeof ENTITY_INTAKE_SECTIONS[number], fieldKey: string): readonly Option[] | undefined {
  if ('options' in section && section.options) {
    const options = section.options as Record<string, readonly Option[]>;
    return options[fieldKey];
  }
  return undefined;
}

/**
 * Gets the option label for a field value
 */
function getOptionLabel(fieldKey: string, value: string | string[] | null, section: typeof ENTITY_INTAKE_SECTIONS[number]): string {
  if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return '(not provided)';
  }

  // Handle array values (multi-select)
  if (Array.isArray(value)) {
    const options = getSectionOptions(section, fieldKey);
    if (options) {
      return value.map((v) => options.find((o) => o.value === v)?.label || v).join(', ');
    }
    return value.join(', ');
  }

  // Handle single values
  const stringValue = String(value);

  // Check section-specific options
  const options = getSectionOptions(section, fieldKey);
  if (options) {
    return options.find((o) => o.value === stringValue)?.label || stringValue;
  }

  // Check state fields
  if (fieldKey === 'state' || fieldKey === 'state_of_residence') {
    return US_STATES.find((s) => s.code === stringValue)?.name || stringValue;
  }

  return stringValue;
}

const AdminEntityFormationViewPage: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EntityIntakeDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    const loadSubmission = async () => {
      if (!submissionId) {
        setError('No submission ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const submission = await loadEntityFormationSubmissionById(submissionId);
        
        if (!submission) {
          setError('Submission not found or access denied');
          return;
        }

        setFormData(submission.data);
        if (submission.submittedAt) {
          setSubmittedAt(submission.submittedAt.toDate());
        }

        // Log entity formation view (may contain sensitive SSN/ITIN data)
        if (user && user.email && role) {
          logDataAccess(
            user.uid,
            user.email,
            role as 'admin' | 'staff' | 'client',
            'entity_formation_submission',
            submissionId,
            `Entity formation form viewed (admin): ${submission.data.legal_business_name || 'N/A'}`,
            true // Mark as sensitive due to potential SSN/ITIN
          ).catch((err: any) => console.error('Failed to log entity formation view:', err));
        }
      } catch (err: any) {
        console.error('Error loading submission:', err);
        setError('Failed to load submission: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (role === 'admin') {
      loadSubmission();
    }
  }, [submissionId, role, user]);

  const renderFieldValue = (fieldKey: EntityIntakeFieldKey, section: typeof ENTITY_INTAKE_SECTIONS[number]): React.ReactNode => {
    if (!formData) return '(not provided)';

    const value = (formData as any)[fieldKey];

    // Handle special field types - owners need special rendering
    if (fieldKey === 'owners') {
      if (!Array.isArray(value) || value.length === 0) {
        return '(not provided)';
      }
      return (
        <div style={{ marginTop: '0.5rem' }}>
          {value.map((owner: any, index: number) => (
            <div key={index} style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '1rem', color: '#495057' }}>
                Owner {index + 1}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '0.5rem 1rem', fontSize: '0.875rem' }}>
                <div style={{ fontWeight: '500', color: '#666' }}>Full Legal Name:</div>
                <div>{owner.full_legal_name || '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>Ownership %:</div>
                <div>{owner.ownership_percentage || '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>Owner Type:</div>
                <div>{owner.owner_type ? OWNER_TYPE_OPTIONS.find((o) => o.value === owner.owner_type)?.label || owner.owner_type : '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>Email:</div>
                <div>{owner.email_address || '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>Phone:</div>
                <div>{owner.phone_number || '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>Street Address:</div>
                <div>{owner.street_address || '(not provided)'}</div>
                
                {owner.street_address_2 && (
                  <>
                    <div style={{ fontWeight: '500', color: '#666' }}>Street Address 2:</div>
                    <div>{owner.street_address_2}</div>
                  </>
                )}
                
                <div style={{ fontWeight: '500', color: '#666' }}>City:</div>
                <div>{owner.city || '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>State:</div>
                <div>{owner.state ? US_STATES.find((s) => s.code === owner.state)?.name || owner.state : '(not provided)'}</div>
                
                <div style={{ fontWeight: '500', color: '#666' }}>ZIP Code:</div>
                <div>{owner.zip_code || '(not provided)'}</div>
                
                {owner.state_of_residence && (
                  <>
                    <div style={{ fontWeight: '500', color: '#666' }}>State of Residence:</div>
                    <div>{US_STATES.find((s) => s.code === owner.state_of_residence)?.name || owner.state_of_residence}</div>
                  </>
                )}
                
                {owner.us_citizen !== null && owner.us_citizen !== undefined && (
                  <>
                    <div style={{ fontWeight: '500', color: '#666' }}>US Citizen:</div>
                    <div>{owner.us_citizen === 'yes' ? 'Yes' : owner.us_citizen === 'no' ? 'No' : '(not provided)'}</div>
                  </>
                )}
                
                {owner.citizenship_status && (
                  <>
                    <div style={{ fontWeight: '500', color: '#666' }}>Citizenship Status:</div>
                    <div>{owner.citizenship_status}</div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (fieldKey === 'signature_date') {
      return value || '(not provided)';
    }

    // Mask SSN/ITIN for security (show only last 4 digits if provided)
    if (fieldKey === 'ssn_itin') {
      const ssnValue = String(value || '');
      if (ssnValue) {
        const last4 = ssnValue.slice(-4);
        return `***-**-${last4}`;
      }
      return '(not provided)';
    }

    return getOptionLabel(fieldKey, value, section);
  };

  if (roleLoading || loading) {
    return (
      <div>
        <Navigation />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (role !== 'admin') {
    return null; // Will redirect via useEffect
  }

  if (error) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            {error}
          </div>
          <button
            onClick={() => navigate('/admin/entity-formation')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ margin: 0 }}>Entity Formation Form (Admin View)</h1>
            {submittedAt && (
              <p style={{ color: '#666', marginTop: '0.5rem' }}>
                Submitted on {submittedAt.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/admin/entity-formation')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Back to Forms
          </button>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          padding: '2rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          {ENTITY_INTAKE_SECTIONS.map((section) => {
            const visibleFields = getVisibleFieldKeysForStep(section.step, formData);
            
            return (
              <div key={section.step} style={{ marginBottom: '2.5rem' }}>
                <h2 style={{
                  marginBottom: '1.5rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #dee2e6',
                  color: '#495057',
                }}>
                  Section {section.step}: {section.title}
                </h2>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {visibleFields.map((fieldKey) => {
                    const label = getFieldLabel(fieldKey);
                    const displayValue = renderFieldValue(fieldKey, section);

                    // Special handling for owners field - it's already formatted with its own layout
                    if (fieldKey === 'owners') {
                      return (
                        <div 
                          key={fieldKey} 
                          style={{ 
                            padding: '0.75rem 0', 
                            borderBottom: '1px solid #eee' 
                          }}
                        >
                          <div style={{ fontWeight: '500', color: '#495057', marginBottom: '0.5rem' }}>{label}:</div>
                          <div style={{ color: '#212529' }}>
                            {displayValue}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div 
                        key={fieldKey} 
                        style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '250px 1fr', 
                          gap: '1rem', 
                          padding: '0.75rem 0', 
                          borderBottom: '1px solid #eee' 
                        }}
                      >
                        <div style={{ fontWeight: '500', color: '#495057' }}>{label}:</div>
                        <div style={{ 
                          color: '#212529',
                          whiteSpace: 'normal',
                        }}>
                          {displayValue}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminEntityFormationViewPage;

